<script setup>
import BankTransferFields from "../../common/BankTransferFields.vue";
import ValidationErrors from "../../common/ValidationErrors.vue";
import Icon from "../../../elements/Icon.vue";
import {usePortalContentAndDesignStore} from "../../../stores/partners/portalContentAndDesignStore";
import {reactive, ref, watch, computed, onMounted} from "vue";
import InvoicePreview from "./InvoicePreview.vue";

const props = defineProps({
            amountUnit: { type: String, default: '' },
            amountWithUnitsFormatted: { type: String, default: '' },
            availableAmount: { type: [Number, String], default: null },
            commissionSelectionEnabled: { type: Boolean, default: false },
            preferredSettings: { type: Object, default: () => ({}) },
            availableMethods: { type: Array, default: () => [] },
            requestErrors: {},
            payoutId: { type: [Number, String], default: null },
            payoutInvoicePath: { type: String, default: null },
            payoutInvoiceSrc: { type: String, default: null },
            payoutInvoiceRequirements: { type: String, default: 'none' },
            billToDetails: { type: Object, default: () => ({
                        first_name: '',
                        last_name: '',
                        company_name: '',
                        address: '',
                        city: '',
                        zip: '',
                        country: '',
                        state: '',
                    }
                )},
    })

    const emit = defineEmits(['request-errors', 'loading-refresh-bank-requirements'])

    const portalContentAndDesignStore = usePortalContentAndDesignStore();

    const placeholders = computed(() => {
        return portalContentAndDesignStore.payoutsPageSettings.default_payout_placeholders
    })

    // Self-billing: the merchant issues the payout document from the partner's
    // tax & billing profile, so guide the partner to complete/review it.
    const selfBillingEnabled = computed(() => !!portalContentAndDesignStore.payoutsPageSettings.self_billing_enabled)
    const taxProfileComplete = computed(() => !!portalContentAndDesignStore.payoutsPageSettings.tax_profile_complete)

    let gatewayCandidate = props.preferredSettings?.gateway || 'paypal';
    const availableMethods = Array.isArray(props.availableMethods) ? props.availableMethods : [];
    gatewayCandidate = availableMethods.includes(gatewayCandidate) ? gatewayCandidate : availableMethods[0] || 'paypal';

    const formErrors = ref({});
    const payment_settings = reactive({
                gateway_paypal: availableMethods.includes('paypal'),
                gateway_bank: availableMethods.includes('bank_transfer'),
                gateway_crypto: availableMethods.includes('crypto'),
                gateway_venmo: availableMethods.includes('venmo'),

                gateway: gatewayCandidate,
                save_for_future_payments: props.preferredSettings?.save_for_future_payments || true,
                bank_transfer_info: {...(props.preferredSettings?.bank_transfer_info || {})},
                paypal_info: {
                    name: props.preferredSettings?.paypal_info?.name || null,
                    username: props.preferredSettings?.paypal_info?.username || null,
                    address: props.preferredSettings?.paypal_info?.address || null,
                    tax_id: props.preferredSettings?.paypal_info?.tax_id || null,
                },
                crypto_info: {
                    type: props.preferredSettings?.crypto_info?.type || null,
                    name: props.preferredSettings?.crypto_info?.name || null,
                    wallet_address: props.preferredSettings?.crypto_info?.wallet_address || null,
                },
                venmo_info: {
                    recipient_type: props.preferredSettings?.venmo_info?.recipient_type || 'USER_HANDLE',
                    phone: props.preferredSettings?.venmo_info?.phone || null,
                    email: props.preferredSettings?.venmo_info?.email || null,
                    user_handle: props.preferredSettings?.venmo_info?.user_handle || null,
                    name: props.preferredSettings?.venmo_info?.name || null,
                }
            })

    const bank_transfer_fields = ref(props.preferredSettings?.bank_transfer_fields || []);
    const refreshBankRequirementsLoading = ref(false)
    const payoutInvoiceUploadUrl = route('partners_int.payouts.invoice-file-upload')
    const payoutInvoiceDeleteUrl = route('partners_int.payouts.invoice-file-delete')
    const invoiceUrl = ref(props.payoutInvoiceSrc || null)
    const invoicePath = ref(props.payoutInvoicePath || null)
    const fileUploadValidationErrors = ref(null)

    // Per-commission selection (partial payouts)
    const commissions = ref([])
    const selectedRewardIds = ref([])
    const commissionsLoading = ref(false)

    const loadCommissions = () => {
        if (!props.commissionSelectionEnabled || !props.amountUnit) {
            return;
        }
        commissionsLoading.value = true;
        axios.get(portalContentAndDesignStore.getPayoutAvailableCommissionsRoute, {
            params: { amount_unit: props.amountUnit }
        })
            .then(response => {
                if (parseInt(response.data.status ?? -1) === 1) {
                    commissions.value = response.data.commissions || [];
                    // Default to the full balance: everything selected.
                    selectedRewardIds.value = commissions.value.map(c => c.reward_id);
                }
            })
            .catch(() => {})
            .finally(() => commissionsLoading.value = false);
    }

    onMounted(loadCommissions)
    watch(() => props.amountUnit, loadCommissions)

    const allSelected = computed(() =>
        commissions.value.length > 0 && selectedRewardIds.value.length === commissions.value.length
    )

    const selectedCommissionsTotal = computed(() =>
        commissions.value
            .filter(c => selectedRewardIds.value.includes(c.reward_id))
            .reduce((sum, c) => sum + Number(c.amount || 0), 0)
    )

    const formattedSelectedTotal = computed(() => {
        const amount = selectedCommissionsTotal.value;
        try {
            return new Intl.NumberFormat('en-US', {
                style: 'currency',
                currency: (props.amountUnit || 'USD').toUpperCase(),
            }).format(amount);
        } catch {
            return `${amount.toFixed(2)} ${(props.amountUnit || '').toUpperCase()}`;
        }
    })

    const toggleSelectAll = () => {
        selectedRewardIds.value = allSelected.value ? [] : commissions.value.map(c => c.reward_id);
    }

    const toggleReward = (rewardId) => {
        const index = selectedRewardIds.value.indexOf(rewardId);
        if (index === -1) {
            selectedRewardIds.value.push(rewardId);
        } else {
            selectedRewardIds.value.splice(index, 1);
        }
    }

    const bankTransferFieldTranslationMapping = reactive({
                targetCurrency: portalContentAndDesignStore.translationSettings.portal_translation_general_target_currency,
                accountHolderName: ((portalContentAndDesignStore.translationSettings.portal_translation_general_account_holder ?? '') + ' ' + (portalContentAndDesignStore.translationSettings.portal_translation_general_full_name ?? '')).trim(),
                accountOrIban: portalContentAndDesignStore.translationSettings.portal_translation_general_account_number + '/' + portalContentAndDesignStore.translationSettings.portal_translation_general_iban,
                routingNumber: portalContentAndDesignStore.translationSettings.portal_translation_general_routing_number,
                swiftBIC: portalContentAndDesignStore.translationSettings.portal_translation_general_swift,
                bankName: portalContentAndDesignStore.translationSettings.portal_translation_general_bank_name,
                bankCountry: ((portalContentAndDesignStore.translationSettings.portal_translation_general_bank ?? '') + ' ' + (portalContentAndDesignStore.translationSettings.portal_translation_general_country ?? '').toLowerCase()).trim(),
                bankCity: ((portalContentAndDesignStore.translationSettings.portal_translation_general_bank ?? '') + ' ' + (portalContentAndDesignStore.translationSettings.portal_translation_general_city ?? '').toLowerCase()).trim(),
                bankAddress: ((portalContentAndDesignStore.translationSettings.portal_translation_general_bank ?? '') + ' ' + (portalContentAndDesignStore.translationSettings.portal_translation_general_address ?? '').toLowerCase()).trim(),
                bankAddressPostalCode: ((portalContentAndDesignStore.translationSettings.portal_translation_general_bank ?? '') + ' ' + (portalContentAndDesignStore.translationSettings.portal_translation_general_postal_code ?? '').toLowerCase()).trim(),
                legalType: portalContentAndDesignStore.translationSettings.portal_translation_general_recipient_type,
                accountNumber: portalContentAndDesignStore.translationSettings.portal_translation_general_account_number,
                BIC: portalContentAndDesignStore.translationSettings.portal_translation_general_swift,
                IBAN: portalContentAndDesignStore.translationSettings.portal_translation_general_iban,
                accountType: portalContentAndDesignStore.translationSettings.portal_translation_general_account_type,
                address__country: portalContentAndDesignStore.translationSettings.portal_translation_general_country,
                address__city: portalContentAndDesignStore.translationSettings.portal_translation_general_city,
                address__firstLine: portalContentAndDesignStore.translationSettings.portal_translation_general_address,
                address__postCode: portalContentAndDesignStore.translationSettings.portal_translation_general_postal_code,
            });

        const uploadDone = (data) => {
            invoiceUrl.value = data.invoice.url;
            invoicePath.value = data.invoice.path;
        }
        const deleteDone = (data) => {
            invoiceUrl.value = null;
            invoicePath.value = null;
        }
        const getAmountUnit = () => {
            return props.amountUnit;
        }
        // Null = full available balance. Otherwise the specific commissions to pay out.
        const getRewardIds = () => {
            if (!props.commissionSelectionEnabled) {
                return null;
            }
            if (allSelected.value) {
                return null;
            }
            return selectedRewardIds.value.slice();
        }
        const getPaymentSettings = () => {
            return payment_settings;
        }
        const changeFutureValue = (value) => {
            payment_settings.save_for_future_payments = value;
        }
        const getInvoicePath = () => {
            return invoicePath.value;
        }
        const validatePaymentInfo = () => {

            let valid = true;
            formErrors.value = {};

            if (props.commissionSelectionEnabled) {
                const round2 = (value) => Math.round(Number(value) * 100) / 100;
                const selectedTotal = round2(selectedCommissionsTotal.value);
                const threshold = round2(portalContentAndDesignStore.payoutsPageSettings?.payout_requirements?.payout_threshold);

                if (selectedRewardIds.value.length === 0 || selectedTotal <= 0) {
                    valid = false;
                    formErrors.value.commissions = true;
                } else if (Number.isFinite(threshold) && threshold > 0 && selectedTotal < threshold) {
                    valid = false;
                    formErrors.value.commissions = true;
                }
            }

            if (payment_settings.gateway === 'paypal') {
                if (inputIsEmpty(payment_settings.paypal_info.name)) {
                    valid = false;
                    formErrors.value.paypal_name = true;
                }
                if (inputIsEmpty(payment_settings.paypal_info.address)) {
                    valid = false;
                    formErrors.value.paypal_address = true;
                }
                if (inputIsEmpty(payment_settings.paypal_info.username) || /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,24}))$/.test(payment_settings.paypal_info.username) !== true) {
                    valid = false;
                    formErrors.value.paypal_username = true;
                }
            }

            if (payment_settings.gateway === 'bank_transfer') {
                bank_transfer_fields.value.forEach((field) => {
                    // If required
                    if(field.required && inputIsEmpty(payment_settings.bank_transfer_info[field.key])) {
                        valid = false;
                        formErrors.value[field.key] = true;
                    }
                });
            }

            if (payment_settings.gateway === 'crypto') {
                if (inputIsEmpty(payment_settings.crypto_info.type)) {
                    valid = false;
                    formErrors.value.crypto_type = true;
                }
                if (inputIsEmpty(payment_settings.crypto_info.name)) {
                    valid = false;
                    formErrors.value.crypto_name = true;
                }
                if (inputIsEmpty(payment_settings.crypto_info.wallet_address)) {
                    valid = false;
                    formErrors.value.crypto_wallet_address = true;
                }
            }

            if (
                (props.payoutInvoiceRequirements === 'required' || props.payoutInvoiceRequirements === 'generate_automatically')
                && (!invoiceUrl.value || !invoicePath.value)
            ) {
                valid = false;
                formErrors.value.invoice = true;
            }

            return valid;
        }
        const inputIsEmpty = (value) => {
            return (value ?? '').trim().length < 1;
        }
        const refreshBankRequirements = (field) => {
            if (!field.refreshRequirementsOnChange || refreshBankRequirementsLoading.value) {
                return;
            }

            // Currency change invalidates every other field (IBAN, BIC,
            // routing number, etc.). Strip them so the refined Wise call
            // doesn't get a mismatched schema and return an empty body.
            // accountHolderName is universal across all Wise rails, so it
            // is preserved.
            if (field.key === 'targetCurrency') {
                Object.keys(payment_settings.bank_transfer_info).forEach((key) => {
                    if (key !== 'targetCurrency' && key !== 'accountHolderName') {
                        delete payment_settings.bank_transfer_info[key];
                    }
                });
            }

            // Clear any stale errors from a previous refresh so switching to a
            // valid value (e.g. a different currency) doesn't keep showing the
            // old error message.
            emit('request-errors', null);
            refreshBankRequirementsLoading.value = true;

            axios.post(portalContentAndDesignStore.getRefreshBankRequirementsRoute, {
                amount_unit: getAmountUnit(),
                settings: {
                    requested_payment_settings: getPaymentSettings()
                }
            })
                .then(response => {
                    const status = parseInt(response.data.status ?? -1);
                    if (status === 1) {
                        setBankTransferFields(response.data.bank_transfer_fields)
                    }
                })
                .catch(error => {
                    if (error.response && error.response.data && error.response?.data?.errors) {
                        emit('request-errors', error.response.data.errors)
                    }
                })
                .finally(() => refreshBankRequirementsLoading.value = false);
        }
        const setBankTransferFields = (refreshed_bank_transfer_fields) => {
            bank_transfer_fields.value = refreshed_bank_transfer_fields
        }

        const invoiceFileName = computed(() => {
            return invoicePath.value ? invoicePath.value.split('/').pop() : null;
        })

        const billFromDetails = computed(() => {
            const gatewayMapping = {
                'paypal': {
                    name: payment_settings.paypal_info.name || '',
                    email: payment_settings.paypal_info.username || '',
                    address: payment_settings.paypal_info.address  || '',
                    taxId: payment_settings.paypal_info.tax_id  || '',
                },
                'bank_transfer': {
                    name: payment_settings.bank_transfer_info.accountHolderName  || '',
                    address: payment_settings.bank_transfer_info.address__firstLine  || '',
                    city: payment_settings.bank_transfer_info.address__city  || '',
                    country: payment_settings.bank_transfer_info.address__country  || '',
                    postalCode: payment_settings.bank_transfer_info.address__postCode  || '',
                    taxId: payment_settings.bank_transfer_info.taxId  || '',
                },
                'crypto': {
                    name: payment_settings.crypto_info.name  || '',
                    address: payment_settings.crypto_info.wallet_address  || '',
                    taxId: payment_settings.crypto_info.taxId  || '',
                }
            };

            return gatewayMapping[payment_settings.gateway];
        })

        const handleInvoiceGenerated = (data) => {
            invoiceUrl.value = data.invoiceUrl;
            invoicePath.value = data.invoicePath;
        }

        const formattedRequestedAmount = computed(() => {
            if (props.commissionSelectionEnabled) {
                return formattedSelectedTotal.value;
            }
            return props.amountWithUnitsFormatted;
        })

        watch(refreshBankRequirementsLoading, (newValue) => {
            emit('loading-refresh-bank-requirements', newValue)
        })

    defineExpose({
        validatePaymentInfo,
        getAmountUnit,
        getRewardIds,
        getPaymentSettings,
        getInvoicePath,
        setBankTransferFields
    })
</script>
<template>
    <div>
        <editable-text-field
            text-type="p"
            class="text-sm text-gray-500 mb-5"
            v-model="portalContentAndDesignStore.translationSettings.portal_translation_features_fields_notice"
        />

        <div class="grid grid-cols-3 gap-8 gap-y-6">

            <!-- Self-billing: guide the partner to their tax & billing profile. -->
            <div v-if="selfBillingEnabled" class="col-span-3">
                <div v-if="!taxProfileComplete"
                     class="flex gap-3 rounded-md bg-amber-50 border border-amber-200 p-3">
                    <svg class="w-5 h-5 shrink-0 mt-0.5 text-amber-500" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                        <path fill-rule="evenodd" d="M8.485 2.495c.673-1.167 2.357-1.167 3.03 0l6.28 10.875c.673 1.167-.17 2.625-1.516 2.625H3.72c-1.347 0-2.189-1.458-1.515-2.625L8.485 2.495ZM10 6a.75.75 0 0 1 .75.75v3.5a.75.75 0 0 1-1.5 0v-3.5A.75.75 0 0 1 10 6Zm0 9a1 1 0 1 0 0-2 1 1 0 0 0 0 2Z" clip-rule="evenodd"/>
                    </svg>
                    <div class="space-y-2">
                        <p class="text-sm text-amber-800">
                            This program issues a self-billing document for each payout. Please complete your
                            tax &amp; billing details before requesting a payout.
                        </p>
                        <router-link :to="{ name: 'affiliate-profile' }"
                                     class="inline-flex items-center gap-1 text-sm font-medium text-amber-900 underline hover:no-underline">
                            Complete tax &amp; billing details
                            <svg class="w-3.5 h-3.5" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                                <path fill-rule="evenodd" d="M7.293 14.707a1 1 0 0 1 0-1.414L10.586 10 7.293 6.707a1 1 0 0 1 1.414-1.414l4 4a1 1 0 0 1 0 1.414l-4 4a1 1 0 0 1-1.414 0Z" clip-rule="evenodd"/>
                            </svg>
                        </router-link>
                    </div>
                </div>
                <div v-else class="flex gap-3 rounded-md bg-gray-50 border border-gray-200 p-3">
                    <svg class="w-5 h-5 shrink-0 mt-0.5 text-gray-400" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                        <path fill-rule="evenodd" d="M18 10a8 8 0 1 1-16 0 8 8 0 0 1 16 0Zm-7-4a1 1 0 1 1-2 0 1 1 0 0 1 2 0ZM9 9a1 1 0 0 0 0 2v3a1 1 0 0 0 1 1h1a1 1 0 1 0 0-2v-3a1 1 0 0 0-1-1H9Z" clip-rule="evenodd"/>
                    </svg>
                    <p class="text-sm text-gray-600">
                        A self-billing document will be issued for this payout from your tax &amp; billing details.
                        <router-link :to="{ name: 'affiliate-profile' }" class="font-medium text-gray-800 underline hover:no-underline">Review or update them</router-link>
                        in your profile.
                    </p>
                </div>
            </div>

            <div class="col-span-3">
                <template v-if="commissionSelectionEnabled">
                    <editable-text-field
                        text-type="label"
                        for-attribute="payout_amount"
                        v-model="portalContentAndDesignStore.translationSettings.portal_translation_general_payout"
                        class="block text-gray-900 text-sm mb-1.5"
                    />

                    <div v-if="commissionsLoading" class="text-sm text-gray-500 py-3">
                        {{ portalContentAndDesignStore.translationSettings.portal_translation_general_loading || 'Loading…' }}
                    </div>

                    <template v-else>
                        <div
                            v-if="commissions.length"
                            class="border rounded divide-y divide-gray-100 max-h-64 overflow-y-auto"
                            :class="(formErrors.commissions ?? false) ? 'border-red-400' : 'border-gray-300'"
                        >
                            <label class="flex items-center gap-3 p-3 bg-gray-50 cursor-pointer select-none">
                                <input
                                    type="checkbox"
                                    :checked="allSelected"
                                    @change="toggleSelectAll"
                                    class="rounded border-gray-300 text-gray-900 focus:ring-0"
                                >
                                <span class="text-sm font-medium">
                                    {{ portalContentAndDesignStore.translationSettings.portal_translation_general_select_all || 'Select all' }}
                                </span>
                                <span class="ml-auto text-sm text-gray-500">{{ amountWithUnitsFormatted }}</span>
                            </label>

                            <label
                                v-for="commission in commissions"
                                :key="commission.reward_id"
                                class="flex items-center gap-3 p-3 cursor-pointer hover:bg-gray-50 select-none"
                            >
                                <input
                                    type="checkbox"
                                    :value="commission.reward_id"
                                    :checked="selectedRewardIds.includes(commission.reward_id)"
                                    @change="toggleReward(commission.reward_id)"
                                    class="rounded border-gray-300 text-gray-900 focus:ring-0"
                                >
                                <span class="flex-1 min-w-0 text-sm text-gray-900 truncate">
                                    {{ commission.customer || commission.customer_email || '—' }}
                                </span>
                                <span class="ml-auto text-sm text-gray-500 whitespace-nowrap">{{ commission.amount_formatted }}</span>
                            </label>
                        </div>
                        <p v-else class="text-sm text-gray-500 py-3">
                            {{ portalContentAndDesignStore.translationSettings.portal_translation_general_no_commissions_available || 'No commissions available.' }}
                        </p>

                        <p class="mt-1.5 text-xs text-gray-500">
                            {{ portalContentAndDesignStore.translationSettings.portal_translation_general_payout || 'Selected' }}:
                            {{ formattedSelectedTotal }}
                            <span class="opacity-70">({{ selectedRewardIds.length }}/{{ commissions.length }})</span>
                        </p>
                    </template>
                </template>
                <div v-else class="bg-gray-200 inline-flex mb-1 p-1 rounded space-x-1 w-full z-0">
                    <label class="bg-white opacity-60 hover:opacity-80 rounded flex-1 py-3 px-5 cursor-pointer">
                        <input type="radio" name="payment_option" value="1" class="sr-only">
                        <div class="text-sm font-medium uppercase">
                            <editable-text-field
                                class="opacity-60 text-xs normal-case font-normal"
                                text-type="span"
                                v-model="portalContentAndDesignStore.translationSettings.portal_translation_general_payout"
                            />
                            {{ amountWithUnitsFormatted }}
                        </div>
                    </label>
                </div>
            </div>

            <div class="col-span-3">
                <div class="bg-gray-200 inline-flex mb-1 p-1 rounded space-x-1 w-full z-0">
                    <label v-if="payment_settings.gateway_paypal" @click="payment_settings.gateway='paypal'"
                           class="rounded flex-1 p-2 cursor-pointer"
                           :class="payment_settings.gateway === 'paypal' ? 'bg-white' : 'opacity-60 hover:opacity-80'"
                    >
                        <div class="flex justify-center items-center text-sm font-medium">PayPal</div>
                    </label>
                    <label v-if="payment_settings.gateway_bank"
                           @click="payment_settings.gateway='bank_transfer'"
                           class="rounded flex-1 p-2 cursor-pointer"
                           :class="payment_settings.gateway === 'bank_transfer' ? 'bg-white' : 'opacity-60 hover:opacity-80'"
                    >
                        <div class="flex justify-center items-center text-sm font-medium">
                            {{ portalContentAndDesignStore.translationSettings.portal_translation_general_bank }}
                        </div>
                    </label>
                    <label v-if="payment_settings.gateway_crypto"
                           @click="payment_settings.gateway='crypto'"
                           class="rounded flex-1 p-2 cursor-pointer"
                           :class="payment_settings.gateway === 'crypto' ? 'bg-white' : 'opacity-60 hover:opacity-80'">
                        <div class="flex justify-center items-center text-sm font-medium">
                            {{ portalContentAndDesignStore.translationSettings.portal_translation_general_crypto }}
                        </div>
                    </label>
                    <label v-if="payment_settings.gateway_venmo"
                           @click="payment_settings.gateway='venmo'"
                           class="rounded flex-1 p-2 cursor-pointer"
                           :class="payment_settings.gateway === 'venmo' ? 'bg-white' : 'opacity-60 hover:opacity-80'">
                        <div class="flex justify-center items-center text-sm font-medium">
                            Venmo
                        </div>
                    </label>
                </div>
            </div>

            <validation-errors class="col-span-3" :errors="requestErrors" />

            <template v-if="payment_settings.gateway_bank && payment_settings.gateway === 'bank_transfer'">
                <bank-transfer-fields
                    :fields="bank_transfer_fields"
                    :info="payment_settings.bank_transfer_info"
                    :errors="formErrors"
                    :label-map="bankTransferFieldTranslationMapping"
                    label-class="block text-gray-900 text-sm mb-1.5"
                    input-extra-class="rounded"
                    @refresh="refreshBankRequirements"
                />
            </template>

            <template v-if="payment_settings.gateway_paypal && payment_settings.gateway === 'paypal'">

                <div class="col-span-3">
                    <editable-text-field
                        text-type="label"
                        for-attribute="full_name"
                        v-model="portalContentAndDesignStore.translationSettings.portal_translation_general_full_name"
                        class="block text-gray-900 text-sm mb-1.5"
                    />
                    <input type="text"
                           id="full_name"
                           name="full_name"
                           v-bind:class="(formErrors.paypal_name ?? false) ? 'border-red-400' : 'border-gray-300 focus:border-gray-400'"
                           class="block w-full rounded border-gray-300 text-sm focus:border-gray-400 focus:ring-0"
                           v-model="payment_settings.paypal_info.name"
                           v-bind:placeholder="placeholders.paypal_name"
                    >
                </div>

                <div class="col-span-3">
                    <editable-text-field
                        text-type="label"
                        for-attribute="email"
                        v-model="portalContentAndDesignStore.translationSettings.portal_translation_general_paypal_email"
                        class="block text-gray-900 text-sm mb-1.5"
                    />
                    <input type="text"
                           id="email"
                           name="email"
                           v-bind:class="(formErrors.paypal_username ?? false) ? 'border-red-400' : 'border-gray-300 focus:border-gray-400'"
                           class="block w-full rounded border-gray-300 text-sm focus:border-gray-400 focus:ring-0"
                           v-model="payment_settings.paypal_info.username"
                           v-bind:placeholder="placeholders.paypal_username"
                    >
                </div>

                <div class="col-span-3">
                    <editable-text-field
                        text-type="label"
                        for-attribute="address"
                        v-model="portalContentAndDesignStore.translationSettings.portal_translation_general_address"
                        class="block text-gray-900 text-sm mb-1.5"
                    />
                    <input type="text"
                           id="address"
                           name="address"
                           v-bind:class="(formErrors.paypal_address ?? false) ? 'border-red-400' : 'border-gray-300 focus:border-gray-400'"
                           class="block w-full rounded border-gray-300 text-sm focus:border-gray-400 focus:ring-0"
                           v-model="payment_settings.paypal_info.address"
                           v-bind:placeholder="placeholders.paypal_address"
                    >
                </div>

                <div class="col-span-3">
                    <editable-text-field
                        text-type="label"
                        for-attribute="tax_id"
                        v-model="portalContentAndDesignStore.translationSettings.portal_translation_general_tax_id_text"
                        class="block text-gray-900 text-sm mb-1.5"
                    />
                    <input type="text"
                           v-bind:class="(formErrors.paypal_tax_id ?? false) ? 'border-red-400' : 'border-gray-300 focus:border-gray-400'"
                           class="block w-full rounded border-gray-300 text-sm focus:border-gray-400 focus:ring-0"
                           v-model="payment_settings.paypal_info.tax_id"
                           v-bind:placeholder="placeholders.paypal_tax_id"
                    >
                </div>
            </template>

            <template v-if="payment_settings.gateway_crypto && payment_settings.gateway === 'crypto'">
                <div class="col-span-3">
                    <editable-text-field
                        text-type="label"
                        for-attribute="crypto_type"
                        v-model="portalContentAndDesignStore.translationSettings.portal_translation_general_crypto_type"
                        class="block text-gray-900 text-sm mb-1.5"
                    />
                    <select
                        v-model="payment_settings.crypto_info.type"
                        v-bind:class="(formErrors.crypto_type ?? false) ? 'border-red-400' : 'border-gray-300 focus:border-gray-400'"
                        class="block w-full text-sm rounded border-gray-300 transition-shadow focus:ring-0 focus:border-gray-400"
                    >
                        <option :value="null">--</option>
                        <option v-for="value in preferredSettings?.crypto_types ?? []" :value="value.value">{{ value.name }}</option>
                    </select>
                </div>

                <div class="col-span-3">
                    <editable-text-field
                        text-type="label"
                        for-attribute="full_name"
                        v-model="portalContentAndDesignStore.translationSettings.portal_translation_general_full_name"
                        class="block text-gray-900 text-sm mb-1.5"
                    />
                    <input type="text"
                           id="full_name"
                           name="full_name"
                           v-bind:class="(formErrors.crypto_name ?? false) ? 'border-red-400' : 'border-gray-300 focus:border-gray-400'"
                           class="block w-full rounded border-gray-300 text-sm focus:border-gray-400 focus:ring-0"
                           v-model="payment_settings.crypto_info.name"
                           v-bind:placeholder="placeholders.crypto_name">
                </div>

                <div class="col-span-3">
                    <editable-text-field
                        text-type="label"
                        for-attribute="wallet_address"
                        v-model="portalContentAndDesignStore.translationSettings.portal_translation_general_wallet_address"
                        class="block text-gray-900 text-sm mb-1.5"
                    />
                    <input type="text"
                           id="wallet_address"
                           name="wallet_address"
                           v-bind:class="(formErrors.crypto_wallet_address ?? false) ? 'border-red-400' : 'border-gray-300 focus:border-gray-400'"
                           class="block w-full rounded border-gray-300 text-sm focus:border-gray-400 focus:ring-0"
                           v-model="payment_settings.crypto_info.wallet_address"
                           v-bind:placeholder="placeholders.wallet_address">
                </div>
            </template>

            <template v-if="payment_settings.gateway_venmo && payment_settings.gateway === 'venmo'">
                <div class="col-span-3">
                    <label
                        for="venmo_recipient_type"
                        class="block text-gray-900 text-sm mb-1.5"
                    >
                        Recipient Type
                    </label>
                    <select
                        v-model="payment_settings.venmo_info.recipient_type"
                        v-bind:class="(formErrors.venmo_recipient_type ?? false) ? 'border-red-400' : 'border-gray-300 focus:border-gray-400'"
                        class="block w-full text-sm rounded border-gray-300 transition-shadow focus:ring-0 focus:border-gray-400"
                    >
                        <option value="USER_HANDLE">Venmo Username</option>
                        <option value="EMAIL">Email Address</option>
                        <option value="PHONE">Phone Number</option>
                    </select>
                </div>

                <div class="col-span-3">
                    <editable-text-field
                        text-type="label"
                        for-attribute="venmo_full_name"
                        v-model="portalContentAndDesignStore.translationSettings.portal_translation_general_full_name"
                        class="block text-gray-900 text-sm mb-1.5"
                    />
                    <input type="text"
                           id="venmo_full_name"
                           name="venmo_full_name"
                           v-bind:class="(formErrors.venmo_name ?? false) ? 'border-red-400' : 'border-gray-300 focus:border-gray-400'"
                           class="block w-full rounded border-gray-300 text-sm focus:border-gray-400 focus:ring-0"
                           v-model="payment_settings.venmo_info.name"
                           placeholder="Full Name">
                </div>

                <div v-if="payment_settings.venmo_info.recipient_type === 'USER_HANDLE'" class="col-span-3">
                    <label
                        for="venmo_user_handle"
                        class="block text-gray-900 text-sm mb-1.5"
                    >
                        Venmo Handle
                    </label>
                    <input type="text"
                           id="venmo_user_handle"
                           name="venmo_user_handle"
                           v-bind:class="(formErrors.venmo_user_handle ?? false) ? 'border-red-400' : 'border-gray-300 focus:border-gray-400'"
                           class="block w-full rounded border-gray-300 text-sm focus:border-gray-400 focus:ring-0"
                           v-model="payment_settings.venmo_info.user_handle"
                           placeholder="@username">
                </div>

                <div v-if="payment_settings.venmo_info.recipient_type === 'EMAIL'" class="col-span-3">
                    <label
                        for="venmo_email"
                        class="block text-gray-900 text-sm mb-1.5"
                    >
                        Email Address
                    </label>
                    <input type="email"
                           id="venmo_email"
                           name="venmo_email"
                           v-bind:class="(formErrors.venmo_email ?? false) ? 'border-red-400' : 'border-gray-300 focus:border-gray-400'"
                           class="block w-full rounded border-gray-300 text-sm focus:border-gray-400 focus:ring-0"
                           v-model="payment_settings.venmo_info.email"
                           placeholder="email@example.com">
                </div>

                <div v-if="payment_settings.venmo_info.recipient_type === 'PHONE'" class="col-span-3">
                    <label
                        for="venmo_phone"
                        class="block text-gray-900 text-sm mb-1.5"
                    >
                        Phone Number
                    </label>
                    <input type="tel"
                           id="venmo_phone"
                           name="venmo_phone"
                           v-bind:class="(formErrors.venmo_phone ?? false) ? 'border-red-400' : 'border-gray-300 focus:border-gray-400'"
                           class="block w-full rounded border-gray-300 text-sm focus:border-gray-400 focus:ring-0"
                           v-model="payment_settings.venmo_info.phone"
                           placeholder="555-123-4567">
                </div>
            </template>

            <file-upload
                v-if="payoutInvoiceRequirements === 'required' || payoutInvoiceRequirements === 'optional' || payoutInvoiceRequirements === 'generate_automatically'"
                class="col-span-3 rounded"
                :class="(formErrors.invoice ?? false) ? 'border border-red-400' : ''"
                :path="invoicePath ?? null"
                :src="invoiceUrl ?? null"
                accept="image/*, .pdf"
                :title="invoiceFileName || (payoutInvoiceRequirements === 'generate_automatically'
                        ? (portalContentAndDesignStore.translationSettings.portal_translation_general_generate_invoice || 'Generate an invoice')
                        : (portalContentAndDesignStore.translationSettings.portal_translation_general_upload_invoice_pdf_or_image || 'Upload an invoice (PDF or Image)'))"
                name="invoice"
                @upload:done="uploadDone"
                @delete:done="deleteDone"
                @upload:error="fileUploadValidationErrors = $event"
                :upload_url="payoutInvoiceUploadUrl"
                :delete_url="payoutInvoiceDeleteUrl"
                :dynamic_action_buttons="true"
                :show_upload_button="payoutInvoiceRequirements!=='generate_automatically'"
                :additional_data="{payoutId: payoutId}"
            >
                <template v-slot:empty_placeholder>
                    <div class="">
                        <span class="text-sm text-gray-500 hidden">No file</span>
                        <icon name="file-missing" size="24" />
                    </div>
                </template>

                <template v-slot:uploaded_placeholder>
                    <div class="">
                        <span class="text-sm text-gray-500 hidden">File present</span>
                        <icon name="file" size="24" />
                    </div>
                </template>
            </file-upload>
            <validation-errors class="col-span-3" :errors="fileUploadValidationErrors" />

            <div v-if="payoutInvoiceRequirements === 'generate_automatically'" class="col-span-3">
                <invoice-preview
                    :payout-id="payoutId"
                    :from-company="billFromDetails"
                    :to-company="billToDetails"
                    :payout-method="payment_settings.gateway"
                    :invoice-number="'to be generated'"
                    :items="[{ name: 'Commission for payout', quantity: 1, unitCost: formattedRequestedAmount }]"
                    :currency="amountUnit"
                    :amount-with-currency-formatted="formattedRequestedAmount"
                    :notes="'Thanks for being an awesome customer!'"
                    :terms="'Please pay by the due date.'"
                    :tax="5"
                    @invoice-generated="handleInvoiceGenerated"
                    @request-errors="requestErrors = $event"
                />
            </div>

            <div class="flex justify-between items-center col-span-3">
                <toggle name="save_payment_info"
                        :modelValue="payment_settings.save_for_future_payments"
                        @update:modelValue="changeFutureValue"
                />
                <span class="flex flex-col flex-grow -mt-px" id="save_payment_info">
                    <editable-text-field
                        text-type="span"
                        v-model="portalContentAndDesignStore.translationSettings.portal_translation_features_save_for_future"
                        :class="['text-sm', portalContentAndDesignStore.getTextColorClass]"
                    />
                </span>
            </div>

        </div>
    </div>
</template>

